import React, { useState, useEffect, useMemo } from 'react';
import { styles } from '../../styles/styles';
import { API_URL } from '../../utils/constants';
import MaterialOutgoingModal from './MaterialOutgoingModal';

// 항목명 목록 (순서대로 01, 02, ...)
const CATEGORIES = [
  'CPU', '쿨러/튜닝', '메인보드', '메모리', '그래픽카드', 
  'SSD', 'HDD', '케이스', '파워', '소프트웨어',
  '모니터', '키보드', '마우스', '사운드바', '스피커',
  'PC헤드셋', '공유기/무선랜', 'IP공유기/허브', '프린터/복합기', '케이블',
  '컨트롤러', '멀티탭', 'ODD', '외장HDD/SSD', 'USB', '기타'
];

// 항목번호 매핑 (01, 02, ...)
const getCategoryCode = (category) => {
  const index = CATEGORIES.indexOf(category);
  return index >= 0 ? String(index + 1).padStart(2, '0') : '99';
};

// 구매처 목록
const SUPPLIERS = ['오픈마켓', '오프라인'];

// 결제방식 목록
const PAYMENT_METHODS = ['카드', '현금'];

function MaterialManagement({ token }) {
  const [materials, setMaterials] = useState([]);
  const [formData, setFormData] = useState({
    inDate: new Date().toISOString().split('T')[0],
    category: '',
    productName: '',
    quantity: 1,
    purchasePrice: '',
    supplyPrice: '',
    vat: '',
    sellingPrice: '',
    supplier: '',
    paymentMethod: '',
    remarks: ''
  });
  const [activeTab, setActiveTab] = useState('stock'); // 'stock', 'incoming', 'all'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // 출고 내역 모달 상태
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [showOutgoingModal, setShowOutgoingModal] = useState(false);

  useEffect(() => {
    if (token) {
      fetchMaterials();
    }
  }, [token]);

  const fetchMaterials = async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/materials`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMaterials(data);
      }
    } catch (err) {
      console.error('자재 목록 로드 실패:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // ID 자동 생성: yymmdd-항목번호-연번
  const generateId = async (category, inDate) => {
    const date = new Date(inDate);
    const yy = String(date.getFullYear()).slice(-2);
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const datePrefix = `${yy}${mm}${dd}`;
    const categoryCode = getCategoryCode(category);
    
    // 같은 날짜, 같은 항목의 기존 자재 수 확인
    const prefix = `${datePrefix}${categoryCode}`;
    const existingCount = materials.filter(m => m.id && String(m.id).startsWith(prefix)).length;
    const sequence = existingCount + 1;
    
    return `${datePrefix}${categoryCode}${sequence}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category || !formData.productName) {
      alert('항목명과 제품명은 필수입니다.');
      return;
    }

    try {
      const id = editingId || await generateId(formData.category, formData.inDate);
      
      const purchase_supply_price = parseFloat(formData.supplyPrice) || 0;
      const purchase_vat = parseFloat(formData.vat) || 0;
      const payload = {
        id,
        in_date: formData.inDate,
        category: formData.category,
        product_name: formData.productName,
        quantity: parseInt(formData.quantity) || 1,
        unit_price: purchase_supply_price + purchase_vat || null,
        purchase_supply_price,
        purchase_vat,
        selling_price: parseFloat(formData.sellingPrice) || null,
        supplier: formData.supplier || null,
        payment_method: formData.paymentMethod || null,
        remarks: formData.remarks || null
      };

      const url = editingId 
        ? `${API_URL}/materials/${editingId}`
        : `${API_URL}/materials`;
      
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        resetForm();
        fetchMaterials();
      } else {
        const error = await response.json();
        alert(error.error || '저장 실패');
      }
    } catch (err) {
      console.error('자재 저장 실패:', err);
      alert('자재 저장에 실패했습니다.');
    }
  };

  const resetForm = () => {
    setFormData({
      inDate: new Date().toISOString().split('T')[0],
      category: '',
      productName: '',
      quantity: 1,
      purchasePrice: '',
      supplyPrice: '',
      vat: '',
      sellingPrice: '',
      supplier: '',
      paymentMethod: '',
      remarks: ''
    });
    setEditingId(null);
  };

  const handleEdit = (material) => {
    const supply = material.purchase_supply_price ?? (material.unit_price ? Math.floor(material.unit_price / 1.1) : '');
    const vat = material.purchase_vat ?? (material.unit_price ? material.unit_price - supply : '');
    setFormData({
      inDate: material.in_date?.split('T')[0] || '',
      category: material.category || '',
      productName: material.product_name || '',
      quantity: material.quantity || 1,
      purchasePrice: material.unit_price || '',
      supplyPrice: supply,
      vat: vat,
      sellingPrice: material.selling_price || '',
      supplier: material.supplier || '',
      paymentMethod: material.payment_method || '',
      remarks: material.remarks || ''
    });
    setEditingId(material.id);
    setActiveTab('all');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('이 자재를 삭제하시겠습니까?')) return;
    try {
      const response = await fetch(`${API_URL}/materials/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchMaterials();
      }
    } catch (err) {
      console.error('자재 삭제 실패:', err);
    }
  };

  // 출고 내역 모달 열기
  const handleShowOutgoing = (material) => {
    setSelectedMaterial(material);
    setShowOutgoingModal(true);
  };

  // 검색 및 필터링
  const filteredMaterials = useMemo(() => {
    return materials.filter(m => {
      const matchSearch = !searchTerm || 
        m.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.supplier?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchCategory = !filterCategory || m.category === filterCategory;
      return matchSearch && matchCategory;
    });
  }, [materials, searchTerm, filterCategory]);

  // 재고 있는 자재만 필터링 (remaining 기준)
  const stockMaterials = useMemo(() => {
    return filteredMaterials.filter(m => (m.remaining ?? m.quantity) > 0);
  }, [filteredMaterials]);

  // 제품별 재고 합산 (재고 현황 탭용)
  const stockSummary = useMemo(() => {
    const summary = {};
    // 제품코드 일련번호 카운터 (카테고리별)
    const codeCounters = {};
    
    filteredMaterials.forEach(m => {
      const key = `${m.category}|${m.product_name}`;
      const inDate = new Date(m.in_date);
      
      if (!summary[key]) {
        // 카테고리별 일련번호 증가
        const catCode = getCategoryCode(m.category);
        if (!codeCounters[catCode]) {
          codeCounters[catCode] = 1;
        }
        const seqNum = String(codeCounters[catCode]).padStart(4, '0');
        codeCounters[catCode]++;
        
        summary[key] = {
          product_code: `${catCode}-${seqNum}`,
          category: m.category,
          product_name: m.product_name,
          total_remaining: 0,     // 현재고
          latest_selling_price: 0,
          latest_in_date: null,
          latest_material_id: null,  // 최신 입고 건 ID (수정용)
          batch_count: 0,
          latest_remarks: '',  // 추가
        };
      }
      summary[key].total_remaining += m.remaining ?? m.quantity ?? 0;
      summary[key].batch_count += 1;
      
      // 가장 최근 입고된 제품의 판매가 사용
      if (!summary[key].latest_in_date || inDate > summary[key].latest_in_date) {
        summary[key].latest_in_date = inDate;
        summary[key].latest_selling_price = parseFloat(m.selling_price) || 0;
        summary[key].latest_material_id = m.id;
        summary[key].latest_remarks = m.remarks || '';  // 추가
      }
    });

    return Object.values(summary).filter(s => s.total_remaining > 0);
  }, [filteredMaterials]);

  // 매입가 입력 시 공급가/부가세 자동 계산
  const handlePurchasePriceChange = (value) => {
    const newFormData = { ...formData, purchasePrice: value };
    
    // 매입가 입력 시 항상 자동 계산 (입력 도중 멈춤 방지)
    const price = parseFloat(value);
    if (!isNaN(price) && value !== '') {
      const supply = Math.round(price / 1.1);
      const vat = price - supply;
      newFormData.supplyPrice = supply;
      newFormData.vat = vat;
    }
    
    setFormData(newFormData);
  };

  // 공급가 입력 시 부가세 자동 계산
  const handleSupplyPriceChange = (value) => {
    setFormData({ 
      ...formData, 
      supplyPrice: value
    });
  };

  // 판매가 인라인 수정 상태
  const [editingPriceKey, setEditingPriceKey] = useState(null);
  const [editingPriceValue, setEditingPriceValue] = useState('');

  // 판매가 수정 시작
  const handleStartEditPrice = (key, currentPrice) => {
    setEditingPriceKey(key);
    setEditingPriceValue(String(Math.floor(currentPrice) || ''));
  };

  // 판매가 수정 저장
  const handleSavePrice = async (materialId, productName, category) => {
    const newPrice = parseFloat(editingPriceValue) || 0;
    
    try {
      // 해당 제품의 모든 입고 건 판매가 일괄 수정
      const targetMaterials = materials.filter(
        m => m.category === category && m.product_name === productName
      );
      
      for (const m of targetMaterials) {
        await fetch(`${API_URL}/materials/${m.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...m,
            in_date: m.in_date?.split('T')[0],
            selling_price: newPrice
          })
        });
      }
      
      fetchMaterials();
      setEditingPriceKey(null);
      setEditingPriceValue('');
    } catch (err) {
      console.error('판매가 수정 실패:', err);
      alert('판매가 수정에 실패했습니다.');
    }
  };

  // 판매가 수정 취소
  const handleCancelEditPrice = () => {
    setEditingPriceKey(null);
    setEditingPriceValue('');
  };

  // 판매가 키보드 이벤트
  const handlePriceKeyDown = (e, materialId, productName, category) => {
    if (e.key === 'Enter') {
      handleSavePrice(materialId, productName, category);
    } else if (e.key === 'Escape') {
      handleCancelEditPrice();
    }
  };

  // 금액 포맷
  const formatCurrency = (value) => {
    if (!value) return '-';
    return Number(value).toLocaleString() + '원';
  };

  // 입력 필드 스타일
  const inputStyle = {
    ...styles.input,
    padding: '8px 10px',
    fontSize: '13px',
    height: '36px'
  };

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer',
    backgroundColor: '#fff'
  };

  const labelStyle = {
    fontSize: '12px',
    color: '#666',
    marginBottom: '4px',
    display: 'block'
  };

  const fieldGroupStyle = {
    display: 'flex',
    flexDirection: 'column'
  };

  return (
    <div style={styles.managementContainer}>
      {/* 등록/수정 섹션 */}
      <section style={{ ...styles.formSection, padding: '20px' }}>
        <h2 style={{ ...styles.sectionTitle, marginBottom: '15px' }}>
          {editingId ? `자재 수정 (${editingId})` : '새 자재 입고'}
        </h2>
        
        <form onSubmit={handleSubmit}>
          {/* 첫 번째 줄: 입고일, 항목명, 제품명, 수량, 매입가, 공급가, 부가세, 판매가 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '12px', marginBottom: '12px' }}>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>입고일 *</label>
              <input
                type="date"
                value={formData.inDate}
                onChange={(e) => setFormData({ ...formData, inDate: e.target.value })}
                style={inputStyle}
                required
              />
            </div>
            
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>항목명 *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                style={selectStyle}
                required
              >
                <option value="">선택</option>
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>제품명 *</label>
              <input
                type="text"
                placeholder="제품명 입력"
                value={formData.productName}
                onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                style={inputStyle}
                required
              />
            </div>
            
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>수량</label>
              <input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                style={inputStyle}
              />
            </div>
            
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>매입가</label>
              <input
                type="number"
                placeholder="0"
                value={formData.purchasePrice}
                onChange={(e) => handlePurchasePriceChange(e.target.value)}
                style={inputStyle}
              />
            </div>

            <div style={fieldGroupStyle}>
              <label style={labelStyle}>공급가</label>
              <input
                type="number"
                placeholder="0"
                value={formData.supplyPrice}
                onChange={(e) => handleSupplyPriceChange(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>부가세</label>
              <input
                type="number"
                placeholder="0"
                value={formData.vat}
                onChange={(e) => setFormData({ ...formData, vat: e.target.value })}
                style={inputStyle}
              />
            </div>

            <div style={fieldGroupStyle}>
              <label style={labelStyle}>판매가</label>
              <input
                type="number"
                placeholder="0"
                value={formData.sellingPrice}
                onChange={(e) => setFormData({ ...formData, sellingPrice: e.target.value })}
                style={inputStyle}
              />
            </div>
          </div>

          {/* 세금 자동 계산 표시 */}
          {formData.supplyPrice && parseFloat(formData.supplyPrice) > 0 && (
            <div style={{
              display: 'flex',
              gap: '20px',
              padding: '10px 15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              marginBottom: '12px',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '13px', color: '#666' }}>
                <strong>매입 총액:</strong>
              </span>
              <span style={{ fontSize: '13px', color: '#495057' }}>
                합계(단가) <strong style={{ color: '#007bff' }}>
                  {(parseFloat(formData.supplyPrice) + (parseFloat(formData.vat) || 0)).toLocaleString()}원
                </strong>
              </span>
              <span style={{ fontSize: '13px', color: '#495057' }}>
                총액(단가×수량) <strong style={{ color: '#dc3545' }}>
                  {((parseFloat(formData.supplyPrice) + (parseFloat(formData.vat) || 0)) * (parseInt(formData.quantity) || 1)).toLocaleString()}원
                </strong>
              </span>
            </div>
          )}

          {/* 두 번째 줄: 구매처, 결제방식, 비고, 버튼 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>구매처</label>
              <select
                value={formData.supplier}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                style={selectStyle}
              >
                <option value="">선택</option>
                {SUPPLIERS.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>결제방식</label>
              <select
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                style={selectStyle}
              >
                <option value="">선택</option>
                {PAYMENT_METHODS.map(pm => (
                  <option key={pm} value={pm}>{pm}</option>
                ))}
              </select>
            </div>
            
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>비고</label>
              <input
                type="text"
                placeholder="비고"
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                style={inputStyle}
              />
            </div>
            
            <div style={{ ...fieldGroupStyle, justifyContent: 'flex-end' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                {editingId && (
                  <button
                    type="button"
                    onClick={resetForm}
                    style={{
                      ...styles.submitButton,
                      flex: 1,
                      backgroundColor: '#6c757d',
                      padding: '8px'
                    }}
                  >
                    취소
                  </button>
                )}
                <button
                  type="submit"
                  style={{
                    ...styles.submitButton,
                    flex: 1,
                    padding: '8px'
                  }}
                >
                  {editingId ? '수정' : '입고'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </section>

      {/* 목록 섹션 - 탭 */}
      <section style={{ ...styles.listSection, marginTop: '20px' }}>
        {/* 탭 버튼 */}
        <div style={{ display: 'flex', gap: '0', marginBottom: '15px', borderBottom: '2px solid #dee2e6' }}>
          <button
            onClick={() => setActiveTab('stock')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderBottom: activeTab === 'stock' ? '2px solid #007bff' : '2px solid transparent',
              marginBottom: '-2px',
              backgroundColor: activeTab === 'stock' ? '#fff' : '#f8f9fa',
              color: activeTab === 'stock' ? '#007bff' : '#666',
              fontWeight: activeTab === 'stock' ? '600' : '400',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            재고 현황 ({stockSummary.length}종)
          </button>
          <button
            onClick={() => setActiveTab('incoming')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderBottom: activeTab === 'incoming' ? '2px solid #28a745' : '2px solid transparent',
              marginBottom: '-2px',
              backgroundColor: activeTab === 'incoming' ? '#fff' : '#f8f9fa',
              color: activeTab === 'incoming' ? '#28a745' : '#666',
              fontWeight: activeTab === 'incoming' ? '600' : '400',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            입고 현황 ({stockMaterials.length}건)
          </button>
          <button
            onClick={() => setActiveTab('all')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderBottom: activeTab === 'all' ? '2px solid #6c757d' : '2px solid transparent',
              marginBottom: '-2px',
              backgroundColor: activeTab === 'all' ? '#fff' : '#f8f9fa',
              color: activeTab === 'all' ? '#6c757d' : '#666',
              fontWeight: activeTab === 'all' ? '600' : '400',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            전체 기록 ({filteredMaterials.length}건)
          </button>
        </div>

        {/* 검색/필터 */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center',
          marginBottom: '15px',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              style={{ ...selectStyle, width: '150px' }}
            >
              <option value="">전체 항목</option>
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="제품명, ID, 구매처 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ ...inputStyle, width: '200px' }}
            />
          </div>
        </div>

        {isLoading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>로딩 중...</div>
        ) : (
          <div style={{ 
            overflowX: 'auto', 
            maxHeight: '500px', 
            overflowY: 'auto', 
            border: '1px solid #dee2e6',
            borderRadius: '4px' 
          }}>
            {/* 재고 현황 탭 */}
            {activeTab === 'stock' && (
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '13px' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={thStyle}>제품코드</th>
                    <th style={thStyle}>항목</th>
                    <th style={thStyle}>제품명</th>
                    <th style={{ ...thStyle, textAlign: 'center', color: '#007bff' }}>현재고</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>개별단가</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>판매가(VAT)</th>
                    <th style={thStyle}>비고</th>
                  </tr>
                </thead>
                <tbody>
                  {stockSummary.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                        재고가 있는 제품이 없습니다.
                      </td>
                    </tr>
                  ) : (
                    stockSummary.map((s, idx) => {
                      const key = `${s.category}|${s.product_name}`;
                      const isEditing = editingPriceKey === key;
                      const priceWithVat = Math.floor(s.latest_selling_price * 1.1);
                      
                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#6c757d' }}>
                            {s.product_code}
                          </td>
                          <td style={tdStyle}>{s.category}</td>
                          <td style={{ ...tdStyle, maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {s.product_name}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '600', color: '#007bff' }}>
                            {s.total_remaining}
                          </td>
                          <td 
                            style={{ 
                              ...tdStyle, 
                              textAlign: 'right',
                              cursor: 'pointer',
                              backgroundColor: isEditing ? '#fff3cd' : 'transparent'
                            }}
                            onClick={() => !isEditing && handleStartEditPrice(key, s.latest_selling_price)}
                            title="클릭하여 수정"
                          >
                            {isEditing ? (
                              <input
                                type="number"
                                value={editingPriceValue}
                                onChange={(e) => setEditingPriceValue(e.target.value)}
                                onKeyDown={(e) => handlePriceKeyDown(e, s.latest_material_id, s.product_name, s.category)}
                                onBlur={handleCancelEditPrice}
                                autoFocus
                                style={{
                                  width: '100px',
                                  padding: '4px 8px',
                                  fontSize: '13px',
                                  textAlign: 'right',
                                  border: '1px solid #ffc107',
                                  borderRadius: '4px'
                                }}
                              />
                            ) : (
                              <span style={{ 
                                padding: '2px 6px', 
                                borderRadius: '4px',
                                backgroundColor: '#f8f9fa',
                                border: '1px dashed #dee2e6'
                              }}>
                                {formatCurrency(s.latest_selling_price)}
                              </span>
                            )}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600', color: '#28a745' }}>
                            {formatCurrency(priceWithVat)}
                          </td>
                          <td style={{ ...tdStyle, maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', color: '#6c757d' }}>
                               {s.latest_remarks || '-'}
                            </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}

            {/* 입고 현황 탭 */}
            {activeTab === 'incoming' && (
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '13px' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={thStyle}>ID</th>
                    <th style={thStyle}>입고일</th>
                    <th style={thStyle}>항목</th>
                    <th style={thStyle}>제품명</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>수량</th>
                    <th style={{ ...thStyle, textAlign: 'center', color: '#007bff' }}>잔여</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>매입가</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>공급가</th>
                    <th style={{ ...thStyle, textAlign: 'right', color: '#dc3545' }}>부가세</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>판매가</th>
                    <th style={thStyle}>구매처</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>출고</th>
                  </tr>
                </thead>
                <tbody>
                  {stockMaterials.length === 0 ? (
                    <tr>
                      <td colSpan={12} style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                        재고가 있는 입고 건이 없습니다.
                      </td>
                    </tr>
                  ) : (
                    stockMaterials.map(m => {
                      const remaining = m.remaining ?? m.quantity;
                      const outQty = m.quantity - remaining;
                      // 세금 계산 (DB에서 오거나 프론트에서 계산)
                      const supplyPrice = m.purchase_supply_price ?? (m.unit_price ? Math.floor(m.unit_price / 1.1) : 0);
                      const vatAmount = m.purchase_vat ?? (m.unit_price ? m.unit_price - supplyPrice : 0);
                      return (
                        <tr key={m.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={tdStyle}>{m.id}</td>
                          <td style={tdStyle}>{m.in_date?.split('T')[0]}</td>
                          <td style={tdStyle}>{m.category}</td>
                          <td style={{ ...tdStyle, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {m.product_name}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>{m.quantity}</td>
                          <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '600', color: '#007bff' }}>
                            {remaining}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(m.unit_price)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: '#17a2b8' }}>{formatCurrency(supplyPrice)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: '#dc3545' }}>{formatCurrency(vatAmount)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(m.selling_price)}</td>
                          <td style={tdStyle}>{m.supplier || '-'}</td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            {outQty > 0 ? (
                              <button
                                onClick={() => handleShowOutgoing(m)}
                                style={{
                                  background: '#e3f2fd',
                                  border: '1px solid #2196f3',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  fontSize: '12px',
                                  color: '#1976d2',
                                  cursor: 'pointer'
                                }}
                              >
                                {outQty}건
                              </button>
                            ) : (
                              <span style={{ color: '#999' }}>-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}

            {/* 전체 기록 탭 */}
            {activeTab === 'all' && (
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '13px' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={thStyle}>ID</th>
                    <th style={thStyle}>입고일</th>
                    <th style={thStyle}>항목</th>
                    <th style={thStyle}>제품명</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>수량</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>잔여</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>매입가</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>공급가</th>
                    <th style={{ ...thStyle, textAlign: 'right', color: '#dc3545' }}>부가세</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>판매가</th>
                    <th style={thStyle}>구매처</th>
                    <th style={thStyle}>결제</th>
                    <th style={thStyle}>비고</th>
                    <th style={thStyle}>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMaterials.length === 0 ? (
                    <tr>
                      <td colSpan={14} style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                        등록된 자재가 없습니다.
                      </td>
                    </tr>
                  ) : (
                    filteredMaterials.map(m => {
                      const remaining = m.remaining ?? m.quantity;
                      const supplyPrice = m.purchase_supply_price ?? (m.unit_price ? Math.floor(m.unit_price / 1.1) : 0);
                      const vatAmount = m.purchase_vat ?? (m.unit_price ? m.unit_price - supplyPrice : 0);
                      return (
                        <tr key={m.id} style={{
                          borderBottom: '1px solid #eee',
                          backgroundColor: remaining === 0 ? '#f5f5f5' : 'transparent'
                        }}>
                          <td style={tdStyle}>{m.id}</td>
                          <td style={tdStyle}>{m.in_date?.split('T')[0]}</td>
                          <td style={tdStyle}>{m.category}</td>
                          <td style={{ ...tdStyle, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {m.product_name}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>{m.quantity}</td>
                          <td style={{
                            ...tdStyle,
                            textAlign: 'center',
                            color: remaining === 0 ? '#999' : '#007bff',
                            fontWeight: remaining > 0 ? '600' : '400'
                          }}>
                            {remaining}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(m.unit_price)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: '#17a2b8' }}>{formatCurrency(supplyPrice)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: '#dc3545' }}>{formatCurrency(vatAmount)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(m.selling_price)}</td>
                          <td style={tdStyle}>{m.supplier || '-'}</td>
                          <td style={tdStyle}>{m.payment_method || '-'}</td>
                          <td style={{ ...tdStyle, maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {m.remarks || '-'}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            <button
                              onClick={() => handleShowOutgoing(m)}
                              style={actionBtnStyle}
                              title="출고내역"
                            >
                              📋
                            </button>
                            <button
                              onClick={() => handleEdit(m)}
                              style={actionBtnStyle}
                              title="수정"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleDelete(m.id)}
                              style={{ ...actionBtnStyle, marginLeft: '4px' }}
                              title="삭제"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </section>

      {/* 출고 내역 모달 */}
      {showOutgoingModal && selectedMaterial && (
        <MaterialOutgoingModal
          material={selectedMaterial}
          token={token}
          onClose={() => {
            setShowOutgoingModal(false);
            setSelectedMaterial(null);
          }}
        />
      )}
    </div>
  );
}

// 테이블 스타일 정의
const thStyle = {
  padding: '12px 8px',
  textAlign: 'left',
  fontWeight: '600',
  whiteSpace: 'nowrap',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #dee2e6'
};

const tdStyle = {
  padding: '10px 8px',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #eee'
};

const actionBtnStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px',
  fontSize: '14px'
};

export default MaterialManagement;
